import { ensureSignedIn, signOut } from './core/AccountGate';
import { Router } from './core/Router';
import { isImmersiveVrSupported } from './core/XrSupport';
import type { AuthUserDto } from './generated/contracts';
import { getSession } from './net/api';
import { showFatalErrorScreen } from './ui/FatalErrorScreen';
import { PortalControls } from './ui/PortalControls';
import { PortalExports } from './ui/PortalExports';
import { PortalHome } from './ui/PortalHome';
import { PortalPeople } from './ui/PortalPeople';
import { sharedTokenFromPath } from './ui/sharedDiagramLink';
import { takeRememberedDestination } from './ui/SignIn';

/**
 * Composition root — now a thin shell around `Router` (`plan/identity-and-portal.md`
 * migration step 3), not the whole app. `mountRoomRoute` below is almost exactly the old
 * `main()` body: everything in it is still built while the entry screen is up — scene, hub
 * connection, XR session — so that pressing "Enter VR" has no work left to do, for the same
 * reason as always (a browser only honours a session request that comes straight from a user
 * gesture, and awaiting a load inside the click handler is how that gesture gets spent). What
 * changed is *when* that body runs at all: only once the router actually activates `/room`,
 * not unconditionally on every page load — a visit to `/portal` now pays for none of it.
 */


/**
 * Where the session lives — a base URL, and deliberately not an origin.
 *
 * **The default has to be right for the arrangement that exists today, not the one being aimed
 * at.** The VR client is a second *document* before it is a second *host*: one Vite build emits
 * both, the dev server serves it at `/vr/`, and so does the deployed API host until `vr.tunet.dev`
 * has a container of its own. Defaulting to a `vr.` subdomain would therefore point every
 * "Open in 3D" at a hostname that does not resolve — in local development, in CI, and in
 * production — while looking exactly like the intended end state.
 *
 * So: same origin, `/vr` path, unless told otherwise. `VITE_TUNET_VR_BASE` is what tells it
 * otherwise, and it becomes `https://vr.tunet.dev` on the deploy that gives the session its own
 * host. Nothing else about this file changes on that day, which is the point of the value
 * existing at all — the same shape `TUNET_ADMIN_HOST` already established for the admin surface.
 *
 * It is a *navigation* target and never a fetch target. Each application talks to its own
 * origin's API and nothing else, which is why this split needs no CORS anywhere.
 */
const VR_BASE = import.meta.env.VITE_TUNET_VR_BASE ?? `${window.location.origin}/vr`;

// Two, and both are this document's own — see index.html, which no longer carries the room's
// markup at all. `#splash` is where `AccountGate` mounts sign-in and where `showFatalError`
// puts the terminal error screen; `#portal` is every route's mount point.
const splashHost = document.querySelector<HTMLElement>('#splash');
const portalHost = document.querySelector<HTMLElement>('#portal');

/**
 * Dev-only inspection handle, filled in as things are constructed rather than at the end —
 * the entry screen is one of the things worth poking at, and by the time boot has finished it
 * is already gone.
 */
const devHandle: Record<string, unknown> = {};

if (import.meta.env.DEV) {
  (window as unknown as { tunet: unknown }).tunet = devHandle;
}

/**
 * Who this browser is signed in as, shared between the router's auth guard and whichever
 * route actually renders. `Router` itself never reads this or calls the server — it only
 * ever sees the `true`/`false` `guard` returns; this is `main.ts`'s own bookkeeping of what
 * that answer was, the "somewhere for a signed-in user's identity to live" the portal shell
 * needs (`plan/identity-and-portal.md`, "The portal is capability-routed").
 */
let account: AuthUserDto | null = null;

/**
 * `Router`'s `guard`. A cheap, synchronous-shaped check against what is already known —
 * `account`, or one `/api/auth/session` round trip the very first time — never a query
 * parameter or anything else the caller could assert (CLAUDE.md, "The server stamps
 * identity"). Returning `false` here is what makes the router call `onUnauthorized` instead
 * of rendering the requested route at all; see `Router.ts`'s own doc comment for why that
 * split — deciding vs. acting on a refusal — is what keeps `Router` testable without a real
 * network call.
 */
async function guard(): Promise<boolean> {
  if (account !== null) {
    return true;
  }

  const existing = await getSession();

  if (existing !== null) {
    account = existing;
    return true;
  }

  return false;
}

/**
 * The terminal error screen — reached from `router.start().catch()` below, and from
 * `safeNavigate`'s own `.catch()` for every in-app navigation, since a rejection from
 * `router.navigate()` (most commonly `mountRoomRoute`'s multi-step boot: a failed
 * `/api/bootstrap`, a broken room snapshot, a hub that refuses to connect) is exactly as
 * fatal as one from `router.start()` — the route that threw has already torn down whatever
 * was on screen before it (`Router.render`'s own cleanup-before-render ordering), so without
 * this the tab would go blank with nothing left to click. Every fatal or blocking screen in
 * this app needs its own way out (the owner got stuck on exactly this once, with the only
 * escape being to type `/portal` into the address bar by hand to reach a sign-out button) —
 * built as DOM nodes, not a template string, so `message` — which can carry a raw server
 * error — never becomes markup.
 */
function showFatalError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);

  if (splashHost) {
    showFatalErrorScreen(splashHost, message, () => void signOut());
  }

  console.error(error);
}

/** `router.navigate`, fire-and-forget, with `showFatalError` as its own `.catch()` — every
 *  in-app navigation site below used to call `void router.navigate(path)` directly, which
 *  left a rejected boot (see `showFatalError`'s own doc comment) as an unhandled promise
 *  rejection with no UI response at all. */
function safeNavigate(path: string): void {
  void router.navigate(path).catch(showFatalError);
}

/**
 * The router's own refusal handler: shows the sign-in screen (the actual "redirect" a
 * refused navigation produces — nothing about the URL changes, `guard` above already stopped
 * that), and once it resolves, retries the exact path that was refused. `router` is declared
 * further down as a top-level `const`, but by the time this function is ever *called* (only
 * from a navigation `router` itself is already handling) it is always initialised — the same
 * "referenced before its own declaration in source order, but never before it exists at
 * runtime" shape `main.ts` already uses elsewhere (see `hub.onResynced`'s own note on
 * `debugPanel`).
 */
function handleUnauthorized(path: string): void {
  if (!splashHost) {
    return;
  }

  void (async () => {
    account = await ensureSignedIn(splashHost);
    safeNavigate(path);
  })();
}

/** The portal's home route (`plan/identity-and-portal.md` migration step 3). */
function mountPortalRoute(mount: HTMLElement): () => void {
  if (account === null) {
    throw new Error('mountPortalRoute reached with no signed-in account — the guard should have prevented this.');
  }

  const portal = new PortalHome(
    mount,
    account,
    // "Open in 3D" leaves this origin. It is a full page navigation, not a router call, because
    // the session is a different application on a different host — see `VR_ORIGIN` above. The
    // diagram id is all that is handed over; the VR host resolves the room for it, which is the
    // job of whoever is going to stand in it.
    (diagramId) => window.location.assign(`${VR_BASE}/?diagram=${encodeURIComponent(diagramId)}`),
    {},
    () => safeNavigate('/portal/exports'),
    () => safeNavigate('/portal/controls'),
    () => safeNavigate('/portal/people'),
  );
  return () => portal.dispose();
}

/**
 * The org owner's People route — the member list, archiving, and the org's invites
 * (`plan/portal-design.md`, "Managing people"; `plan/auth-and-org-admission.md`, "Correction
 * ten").
 *
 * Not gated here, and that is deliberate rather than an oversight: the guard this route needs is
 * a role, and `Router`'s guard is about a session. Every route the page calls answers a 403 to a
 * plain member, which is where the refusal belongs — a client-side route guard would be a second
 * copy of a rule the server already owns, and the one that could silently disagree with it. The
 * rail simply does not offer the destination to a member.
 */
function mountPortalPeopleRoute(mount: HTMLElement): () => void {
  if (account === null) {
    throw new Error(
      'mountPortalPeopleRoute reached with no signed-in account — the guard should have prevented this.',
    );
  }

  const peoplePage = new PortalPeople(mount, account, () => safeNavigate('/portal'));
  return () => peoplePage.dispose();
}

/** The portal's exports route (`plan/identity-and-portal.md`, "Exports, as the portal's
 *  first real page"). */
function mountPortalExportsRoute(mount: HTMLElement): () => void {
  if (account === null) {
    throw new Error(
      'mountPortalExportsRoute reached with no signed-in account — the guard should have prevented this.',
    );
  }

  const exportsPage = new PortalExports(mount, account, () => safeNavigate('/portal'));
  return () => exportsPage.dispose();
}

/** The portal's controls route — a plain read of `ControlBindings.ts`, so it can never
 *  drift from what the in-headset panel shows (`PortalControls.ts`'s own module comment). */
function mountPortalControlsRoute(mount: HTMLElement): () => void {
  if (account === null) {
    throw new Error(
      'mountPortalControlsRoute reached with no signed-in account — the guard should have prevented this.',
    );
  }

  const controlsPage = new PortalControls(mount, account, () => safeNavigate('/portal'));
  return () => controlsPage.dispose();
}

/**
 * The desktop diagram viewer (plan/diagram-extraction.md, A5) — read-only, sessionless in the
 * room sense (no hub connection, no presence, no Babylon scene shared with `/room`), reached by
 * link.
 *
 * Which diagram rides in the query string for the identical reason `/room`'s own room id does:
 * `Router` is deliberately exact-path-only and matches on `location.pathname` alone, so
 * `?diagramId=` is invisible to it (see `Router.ts`'s own doc on `pathnameOf`). A visit with no
 * id has nothing to fall back to — unlike `/room`, which resolves the caller's default room from
 * `/api/bootstrap` — because "the caller's default diagram" is not a thing this product has a
 * notion of; the portal's room rows are what carry the ids.
 *
 * **Loaded on demand, and that is the only reason the portal is cheap.** `DiagramViewer` reaches
 * Babylon through `DiagramViewport` — the render stack the viewer genuinely needs and shares with
 * the room — so a static import here would put the engine in the entry chunk and hand it to every
 * visitor who only ever opens `/portal` or signs in. `await import()` makes it a chunk of its
 * own, fetched when somebody actually opens a diagram.
 */
async function mountDiagramRoute(mount: HTMLElement): Promise<() => void> {
  if (account === null) {
    throw new Error('mountDiagramRoute reached with no signed-in account — the guard should have prevented this.');
  }

  const { DiagramViewer } = await import('./ui/DiagramViewer');

  const diagramId = new URLSearchParams(window.location.search).get('diagramId');

  if (diagramId === null) {
    // Not a fatal error screen: a bare `/diagram` is a stale bookmark or a truncated link, and
    // the portal is where the real ones are.
    safeNavigate('/portal');
    return () => {};
  }

  const viewer = new DiagramViewer(mount, account, diagramId, () => safeNavigate('/portal'));
  return () => viewer.dispose();
}

const router = new Router({
  mount: portalHost ?? document.body,
  routes: {
    '/portal': mountPortalRoute,
    '/portal/people': mountPortalPeopleRoute,
    '/portal/exports': mountPortalExportsRoute,
    '/portal/controls': mountPortalControlsRoute,
    '/diagram': mountDiagramRoute,
  },
  guard,
  onUnauthorized: handleUnauthorized,

  // No `resolveDefault`. It existed to answer "which route does a bare / mean", and the only
  // answer that ever varied was "the room, if this device can do VR" — which is not a route on
  // this host any more. The capability hand-off moved to the bottom of this file, where it can
  // navigate to another origin rather than having to return a path.

  // A stale or mistyped link lands on the portal rather than nothing — see this file's
  // routes table above: there is nothing else it could sensibly mean.
  fallback: '/portal',
});

/**
 * Where a completed Google sign-in was actually aiming, if it was aiming anywhere.
 *
 * **The redirect is what makes this necessary.** Sign-in used to settle in place, so
 * `handleUnauthorized` could retry the exact path that was refused — the URL never changed
 * throughout. A Google handshake is a full-page navigation out to `accounts.google.com` and back
 * to a fixed `/`, so `/room?roomId=…` is gone by the time this file runs again, and every deep
 * link would otherwise land on the portal home.
 *
 * **The destination never leaves this browser, and the server is never told.**
 * `GoogleSignIn.SignedInDestination` is deliberately a fixed relative path rather than a
 * caller-supplied `returnUrl` — the admin surface's own precedent, and an open redirect is not
 * worth the convenience. The path is parked in `sessionStorage` by the sign-in screen and
 * validated against this app's own routes on the way back out (`takeRememberedDestination`), so
 * this is a decision the client makes about itself rather than one the server is asked to trust.
 *
 * **Read unconditionally, whether or not one is found.** Leaving it parked across an abandoned
 * sign-in would hand it to a later, unrelated load; taking it here means at worst one redirect
 * within the same tab session, to a path this browser did ask for.
 */
const remembered = takeRememberedDestination();

// The one place a genuinely first-load failure lands (a broken /api/auth/session before the
// splash or anything else exists) — everything past this point is `showFatalError`'s own
// concern, including every other rejection this file can produce (`safeNavigate`).
//
// `navigate` rather than `start` for a restored destination, so the deep link replaces the
// callback's own `/` in the address bar rather than sitting behind it.
/**
 * A share link (`/s/{token}`), which is **not a route** and deliberately never reaches `Router`.
 *
 * Two reasons, and the second is the one that matters. First, mechanically: `Router` is
 * exact-path-only by design (`Router.ts`) and a token is a path *segment*, so `/s/abc…` matches
 * nothing and falls straight through to the `/portal` fallback. Second, and decisively: every
 * navigation `Router` handles goes through `guard`, which asks the server who is signed in and
 * shows the sign-in screen when the answer is nobody. A share link's whole premise is that the
 * person holding it has no account here — routing it would ask a stranger to sign in to see a
 * page that needs no session at all, and the `/portal` fallback would then hand them somebody
 * else's org's front door.
 *
 * So it is handled here, ahead of the router, and the router is disposed rather than left
 * listening: nothing on this page navigates in-app, and a `popstate` reaching a router whose
 * fallback is `/portal` is the same wrong answer arriving by the back button.
 */
const sharedToken = sharedTokenFromPath(window.location.pathname);

/**
 * A bare `/` on a device that can actually run an immersive session leaves for the VR host.
 *
 * **Only for a bare `/`, and only when there is nothing else this load was asked to do.** Every
 * other entry — a share link, a parked deep link, any real path — is what the visitor typed or
 * was sent, and rewriting it would be this application deciding it knows better. The
 * capability check is the same one that used to pick between the room route and the portal
 * here; what changed is that the room is not a route on this host any more, so the answer is a
 * different origin rather than a different path.
 *
 * `assign`, not `replace`: the back button should return to whatever sent them here.
 */
function shouldHandOffToVr(): boolean {
  return (
    sharedToken === null &&
    remembered === null &&
    (window.location.pathname === '/' || window.location.pathname === '')
  );
}

if (sharedToken !== null) {
  router.dispose();

  // Loaded here rather than imported at the top: this page reaches Babylon through
  // `DiagramViewport`, and a static import would hand the engine to every visitor, on every
  // load, to serve the one path in the application that actually needs it.
  void import('./ui/SharedDiagramPage')
    .then(({ SharedDiagramPage }) => new SharedDiagramPage(portalHost ?? document.body, sharedToken))
    .catch(showFatalError);
} else if (remembered !== null) {
  safeNavigate(remembered);
} else {
  void (async () => {
    if (shouldHandOffToVr() && (await isImmersiveVrSupported())) {
      // THE TRAILING SLASH IS LOAD-BEARING, and its absence was a real bug caught by trying it.
      // `VR_BASE` defaults to `${origin}/vr`, a *directory*, and both servers resolve a directory
      // to its index only when asked for the directory: `/vr` reaches the web app's own index
      // instead — the same document, the wrong application, and nothing about the result says so.
      // Measured against the dev server: `/vr` answers 200 with <title>Tunet</title> and `/vr/`
      // answers 200 with <title>Tunet — session</title>.
      //
      // Harmless when `VR_BASE` is a real origin (`https://vr.tunet.dev/` is just the root), so
      // one form is correct for both topologies.
      window.location.assign(`${VR_BASE}/`);
      return;
    }

    await router.start();
  })().catch(showFatalError);
}
